/**
 * `MentionPicker` — the @-mention autocomplete dropdown, rendered
 * absolutely-positioned ABOVE the composer (`bottom-full`) when the lawyer
 * types `@<filter>` at the caret. Claude-Code-style: a scrollable list of
 * matching workspace files (basename + zone hint), the active row
 * highlighted in gold, picked via click or keyboard (↑/↓ + Enter/Tab,
 * driven by the parent `ChatInput`).
 *
 * Presentational only — all state (open/filter/activeIndex) lives in
 * `ChatInput`. Selection fires on `onMouseDown` with `preventDefault()` so
 * the textarea KEEPS focus (the textarea's blur-to-close never fires
 * before the pick lands).
 */

import { FileText } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import type { MentionHit, MentionedFile } from "@/lib/workspace/mentions-directive";

interface MentionPickerProps {
  /** Stable id for the listbox — the textarea's `aria-controls` +
   *  `aria-activedescendant` target it for the combobox ARIA relationship. */
  listboxId: string;
  results: MentionHit[];
  /** Index of the keyboard-highlighted row (parent-owned). */
  activeIndex: number;
  onSelect: (file: MentionedFile) => void;
  /** Sync the highlight to the hovered row so mouse + keyboard agree. */
  onHoverIndex: (index: number) => void;
}

/** The parent directory of a zone-relative path — the dim "where" hint
 *  shown right-aligned in each row. "" (root file) renders nothing. */
function zoneHintOf(path: string): string {
  const slash = path.lastIndexOf("/");
  return slash > 0 ? path.slice(0, slash) : "";
}

export function MentionPicker({
  listboxId,
  results,
  activeIndex,
  onSelect,
  onHoverIndex,
}: MentionPickerProps) {
  const { t } = useTranslation();

  return (
    <div
      data-testid="composer-mention-picker"
      className="absolute bottom-full left-0 right-0 z-50 mb-2 overflow-hidden rounded-lg border border-cream-200/80 bg-white shadow-[0_8px_24px_rgba(28,24,20,0.12)] dark:border-coffee-700/70 dark:bg-coffee-900"
    >
      {results.length === 0 ? (
        // role=status + aria-live so "No files match" is ANNOUNCED to AT
        // (the empty row is otherwise silent). Finding [4].
        <div
          role="status"
          aria-live="polite"
          className="px-3 py-2 text-caption text-cream-500"
          data-testid="composer-mention-empty"
        >
          {t("composer.mentions.dropdownEmpty")}
        </div>
      ) : (
        <ul
          id={listboxId}
          role="listbox"
          aria-label={t("composer.mentions.listboxLabel")}
          className="max-h-64 overflow-y-auto py-1"
        >
          {results.map((f, i) => {
            const hint = zoneHintOf(f.path);
            const selected = i === activeIndex;
            return (
              <li key={f.path} role="presentation">
                <button
                  type="button"
                  id={`${listboxId}-opt-${i}`}
                  role="option"
                  aria-selected={selected}
                  // Focus stays on the textarea (aria-activedescendant
                  // pattern) — options are not in the tab order.
                  tabIndex={-1}
                  data-testid="composer-mention-option"
                  data-path={f.path}
                  // preventDefault keeps the textarea focused — see file doc.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onSelect(f);
                  }}
                  onMouseEnter={() => onHoverIndex(i)}
                  className={cn(
                    "flex w-full items-center gap-2 px-3 py-1.5 text-left text-[13px] transition-colors",
                    selected
                      ? "bg-gold-400/15 text-coffee-900 dark:bg-gold-900/30 dark:text-cream-100"
                      : "text-coffee-700 dark:text-cream-300",
                  )}
                >
                  <FileText
                    size={13}
                    className="shrink-0 text-gold-700/80 dark:text-gold-400/80"
                    aria-hidden
                  />
                  {/* TAG-FIRST (A8): `@tag` (mono) when tagged, else the name; the
                      title reads as the muted secondary so the lawyer can identify it. */}
                  <span className="flex min-w-0 flex-1 items-baseline gap-1.5">
                    <span className={cn("shrink-0 truncate font-medium", f.tag && "font-mono")}>
                      {f.tag ? f.tag : (f.title ?? f.name)}
                    </span>
                    {/* Echo the field the hit MATCHED on. Typing an uploaded
                        filename must not surface a row whose visible labels
                        contain none of it — that reads as a wrong match. */}
                    {f.matchedOn === "originalName" && f.originalName ? (
                      <span
                        data-testid="composer-mention-original"
                        className="min-w-0 truncate text-[12px] text-coffee-500 dark:text-cream-400"
                        title={f.originalName}
                      >
                        {f.originalName}
                      </span>
                    ) : (
                      f.tag &&
                      f.title && (
                        <span className="min-w-0 truncate text-[12px] text-coffee-500 dark:text-cream-400">
                          {f.title}
                        </span>
                      )
                    )}
                  </span>
                  {hint && (
                    <span className="ml-auto shrink-0 truncate pl-2 text-[11px] text-cream-500">
                      {hint}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
